import { Editor, Extension } from '@tiptap/core';
import debounce from 'lodash/debounce';
import { safeSessionStorage } from '@ifixit/utils';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      sessionStorage: {
         clearSessionStorage: () => ReturnType;
      };
   }
}

const getDocID = (editorId: string) => `${editorId}_${window.location.pathname}`;

const getStorageKey = (editorId: string) => {
   const docId = getDocID(editorId);
   return `prosemirror_editor_doc_${docId}`;
};

const getFromStorage = (editorId: string) => {
   const storageKey = getStorageKey(editorId);
   try {
      return safeSessionStorage.getJson(storageKey);
   } catch {
      return null;
   }
};

const createDebouncedAutosave = (wait: number) =>
   debounce((editorId: string, editor: Editor) => {
      const storageKey = getStorageKey(editorId);
      const json = JSON.stringify(editor.getJSON());
      safeSessionStorage.setItem(storageKey, json);
   }, wait);

export const SessionStorage = Extension.create({
   name: 'sessionStorage',

   onCreate() {
      const contentFromStorage = getFromStorage(this.options.editorId);
      if (this.editor.isEmpty && contentFromStorage) {
         this.editor.commands.setContent(contentFromStorage, { emitUpdate: false });
      }

      this.storage.autosave = createDebouncedAutosave(this.options.autosaveWait);
   },
   onUpdate() {
      this.storage.autosave(this.options.editorId, this.editor);
   },
   addStorage() {
      return {
         autosave: null,
      };
   },
   addCommands() {
      return {
         clearSessionStorage: () => () => {
            const storageKey = getStorageKey(this.options.editorId);
            safeSessionStorage.removeItem(storageKey);
            return true;
         },
      };
   },
});
